import * as React from "react"
import { keepStill } from "./still"

/** The Dutch Interactive Award, turning: a rendered video (desktop landscape,
 *  phones portrait), played by the scroll. Every frame is a keyframe, and it's
 *  played from memory (fetched whole once), so it follows the scroll exactly. */

/** appear: its opacity; turn: through its turn (0..1); tail: on into its own fade to black (0..1) */
export type AwardApi = { set: (appear: number, turn: number, tail?: number) => void }

/** where the award's foot is in the phone video (0..1 of its height), measured */
export const AWARD_FOOT = 0.703
/** the phone video's size */
export const AWARD_TALL = { w: 720, h: 1280 }

/** how far into the video it's still lit (it fades to black over its last frames) */
const LIT = 0.86

export const awardTall = (vw: number, vh: number) => vw / vh < 0.9

export default function AwardVideo({ api }: { api: React.MutableRefObject<AwardApi | null> }) {
    const video = React.useRef<HTMLVideoElement>(null)
    // (desktop: the latest render)
    const src = React.useMemo(() => (awardTall(innerWidth, innerHeight) ? "720" : "n-1920"), [])
    React.useLayoutEffect(() => {
        const v = video.current!
        let ready = false,
            url = "",
            gone = false,
            appear = 0,
            turn = 0,
            tail = 0
        // one seek at a time: the next once the last is drawn
        let pending: number | null = null
        const seek = (t: number) => {
            if (v.seeking) return void (pending = t)
            if (Math.abs(v.currentTime - t) > 0.001) v.currentTime = t
        }
        const onSeeked = () => {
            if (pending == null) return
            const t = pending
            pending = null
            seek(t)
        }
        v.addEventListener("seeked", onSeeked)
        // (it only ever plays for a moment, for iOS; any other playing is stopped
        // at once, and it goes back to where the scroll is: still.ts)
        const still = keepStill(v)
        const show = () => {
            v.style.opacity = appear.toFixed(3)
            v.style.visibility = appear > 0 ? "visible" : "hidden"
            // (up to the last frame it's fully lit: the video fades to black after)
            if (ready && isFinite(v.duration)) seek(Math.max(0, (turn * LIT + tail * (0.985 - LIT)) * v.duration))
        }
        // fetched whole and played from memory (every frame reachable), the
        // format this browser plays first
        const files = [`/about/award-${src}.mp4`, `/about/award-${src}.webm`]
        if (v.canPlayType('video/mp4; codecs="avc1.640028"') !== "probably" && v.canPlayType('video/webm; codecs="vp9"')) files.reverse()
        const load = (k: number) => {
            if (k >= files.length || gone) return
            fetch(files[k])
                .then((r) => (r.ok ? r.blob() : Promise.reject()))
                .then((b) => {
                    if (gone) return
                    if (url) URL.revokeObjectURL(url)
                    url = URL.createObjectURL(b)
                    const onMeta = () => (clean(), (ready = true), show())
                    const onError = () => (clean(), load(k + 1))
                    const clean = () => (v.removeEventListener("loadedmetadata", onMeta), v.removeEventListener("error", onError))
                    v.addEventListener("loadedmetadata", onMeta)
                    v.addEventListener("error", onError)
                    v.src = url
                    v.load()
                    // (iOS draws a video's frames only once it has played: a moment, muted)
                    still.prime()
                })
                .catch(() => load(k + 1))
        }
        // (after the page's own video: no need to compete with it at first)
        const start = setTimeout(() => load(0), 1200)
        const onPause = () => show()
        v.addEventListener("pause", onPause)
        api.current = {
            set(a, t, e = 0) {
                appear = a
                turn = t
                tail = e
                show()
            },
        }
        return () => {
            gone = true
            still.release()
            clearTimeout(start)
            api.current = null
            v.removeEventListener("seeked", onSeeked)
            v.removeEventListener("pause", onPause)
            if (url) URL.revokeObjectURL(url)
        }
    }, [])
    return (
        <video
            ref={video}
            className="aw"
            poster={`/about/award-${src}-poster.jpg`}
            muted
            playsInline
            preload="none"
            disablePictureInPicture
            aria-label="The Dutch Interactive Award for Best in Design, turning"
        />
    )
}
